import React from 'react';
import { useAssessmentData } from './AssessmentDataContext';
import { winRateColor } from '../../utils/colors';
import { SectionHeader, ScrollTable, BucketPill, DeltaBadge, MIN_RELIABLE_N, winRateGlowStyle, CALL_BUCKETS } from './shared';

// Extracted verbatim from Assessment.js's WindowsBreakdownTable component +
// its `activeTab === 'windows'` render block (Tech Debt 2026-09,
// "Assessment and Backtest are god files" -- remaining-7-tabs context lift).
// Reads shared state from useAssessmentData() instead of via props.

function WindowsBreakdownTable({ windows, activeWindows = [], showCalls, showPuts }) {
  const keyPeriods = ['3d', '5d', '7d', '30d', '60d'];
  if (!windows || windows.length === 0) {
    return <p className="text-gray-500 text-sm text-center py-8">No window data available. Run <code className="font-mono text-xs bg-white/[0.04] text-gray-300 px-1 py-0.5 rounded">trader assess --force</code> to generate.</p>;
  }
  const activeWindowByLabel = {};
  (activeWindows || []).forEach(w => { activeWindowByLabel[w.label] = w; });

  const BUCKET_ORDER = ['95+', '90+', '85+', '80+', '75+', '70+', '<25', '<20', '<15', '<10', '<5'];
  const allBuckets = BUCKET_ORDER.filter(bk => {
    const side = CALL_BUCKETS.has(bk) ? 'call' : 'put';
    if (side === 'call' && !showCalls) return false;
    if (side === 'put'  && !showPuts)  return false;
    return windows.some(w => w.buckets.some(b => b.bucket === bk));
  });

  return (
    <ScrollTable minWidth={500}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08]">
            <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-20">Bucket</th>
            {windows.map(w => (
              <th key={w.label} colSpan={keyPeriods.length + 1}
                  className="text-center py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 border-l border-white/[0.06]">
                {w.label}
                <span className="block text-[10px] text-gray-600 font-normal font-mono tabular-nums normal-case tracking-normal">{w.total_peaks.toLocaleString()} peaks</span>
              </th>
            ))}
          </tr>
          <tr className="border-b border-white/[0.08]">
            <th className="py-1 px-3" />
            {windows.map(w => (
              <React.Fragment key={w.label}>
                <th className="py-1 px-2 text-right text-gray-600 text-[10px] font-normal border-l border-white/[0.06] min-w-[28px]">N</th>
                {keyPeriods.map(p => (
                  <th key={p} className="py-1 px-2 text-right text-gray-600 text-[10px] font-normal min-w-[46px]">WR{p}</th>
                ))}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {allBuckets.map((bk) => (
            <tr key={bk} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
              <td className="py-2 px-3"><BucketPill bucket={bk} /></td>
              {windows.map(w => {
                const brow = w.buckets.find(b => b.bucket === bk);
                const activeBrow = activeWindowByLabel[w.label]?.buckets?.find(b => b.bucket === bk);
                return (
                  <React.Fragment key={w.label}>
                    <td className="py-2 px-2 text-right text-gray-500 font-mono tabular-nums text-[11px] border-l border-white/[0.04] whitespace-nowrap">
                      {brow ? brow.sample_count.toLocaleString() : '—'}
                      <DeltaBadge curr={brow?.sample_count} prev={activeBrow?.sample_count} decimals={0} suffix="" />
                    </td>
                    {keyPeriods.map(p => {
                      const vRaw = brow?.win_rate?.[p];
                      const lowN = !brow || brow.sample_count < MIN_RELIABLE_N;
                      const v = lowN ? null : vRaw;
                      const pv = activeBrow?.win_rate?.[p];
                      const glow = winRateGlowStyle(v);
                      return (
                        <td key={p} className="py-2 px-2 text-right align-middle whitespace-nowrap">
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded-md font-mono tabular-nums text-[12px] ${winRateColor(v)}`}
                            style={glow}>
                            {v != null ? `${v.toFixed(1)}%` : '—'}
                          </span>
                          <DeltaBadge curr={v} prev={pv} />
                        </td>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollTable>
  );
}



export default function WindowsTab() {
  const { data, activeData, showCalls, showPuts } = useAssessmentData();
  return (
  <>
    <SectionHeader>Win rates by assessment window (WR7d / WR30d / WR60d)</SectionHeader>
    <p className="text-[12px] text-gray-500 mb-4 leading-relaxed">
      Each column group shows results for that lookback window. Wider windows include more history but may reflect older market regimes. WR = scaled barrier-touch win rate.
    </p>
    <WindowsBreakdownTable
      windows={data.windows}
      activeWindows={activeData?.windows || []}
      showCalls={showCalls}
      showPuts={showPuts}
    />
  </>
  );
}
